import '@/i18n'
import { ConvexBetterAuthProvider } from '@convex-dev/better-auth/react'
import { env } from '@goaltree/env/web'
import * as Sentry from '@sentry/react'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { ConvexReactClient } from 'convex/react'
import { PostHogProvider } from 'posthog-js/react'
import ReactDOM from 'react-dom/client'

import { authClient } from '@/lib/auth-client'
import { captureFirstTouch } from '@/lib/first-touch'
import { posthog } from '@/lib/posthog'
import '@/lib/pwa'
import Loader from './components/loader'
import { routeTree } from './routeTree.gen'

if (env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.2,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD,
  })
}

captureFirstTouch()

const convex = new ConvexReactClient(env.VITE_CONVEX_URL)

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Restore scroll per route (keyed by pathname) so switching tabs and returning
  // keeps your place. Custom scroll containers opt in via data-scroll-restoration-id.
  scrollRestoration: true,
  getScrollRestorationKey: (location) => location.pathname,
  defaultPendingComponent: () => <Loader />,
  context: {},
  Wrap: function WrapComponent({ children }: { children: React.ReactNode }) {
    return (
      <ConvexBetterAuthProvider client={convex} authClient={authClient}>
        <PostHogProvider client={posthog}>{children}</PostHogProvider>
      </ConvexBetterAuthProvider>
    )
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Fallback for a habit-notification action the service worker couldn't post in
// the background (e.g. Android blocking network from a swiped-away app): the SW
// opens the app with the action encoded, and we complete it here on load.
{
  const params = new URLSearchParams(window.location.search)
  const action = params.get('habitAction')
  const token = params.get('habitToken')
  const actionUrl = params.get('habitActionUrl')
  if (action && token && actionUrl) {
    void fetch(actionUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ token, action }),
    }).catch(() => {})
    for (const k of ['habitAction', 'habitToken', 'habitActionUrl']) params.delete(k)
    const qs = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
  }
}

if ('serviceWorker' in navigator) {
  // Installed PWAs (esp. Android) can hold a stale SW for a long time. Check for
  // an update on every load; when an updated SW takes control, reload once so the
  // latest notification-handling code is actually running.
  const hadController = !!navigator.serviceWorker.controller
  navigator.serviceWorker
    .register('/sw.js')
    .then((reg) => reg.update().catch(() => {}))
    .catch(() => {})
  let reloadedForSW = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloadedForSW || !hadController) return // skip first-install activation
    reloadedForSW = true
    window.location.reload()
  })
}

const rootElement = document.getElementById('app')

if (!rootElement) {
  throw new Error('Root element not found')
}

if (!rootElement.dataset.mounted) {
  rootElement.dataset.mounted = '1'
  const root = ReactDOM.createRoot(rootElement)
  root.render(<RouterProvider router={router} />)
  dismissBootSplash(rootElement)
}

// index.html's boot splash covers start-up (blank router/auth gaps included) and
// fades once the app shows a real screen — anything but the in-app Loader.
function dismissBootSplash(app: HTMLElement) {
  const splash = document.getElementById('boot-splash')
  if (!splash) return
  let done = false
  const hide = () => {
    if (done) return
    done = true
    observer.disconnect()
    splash.style.opacity = '0'
    setTimeout(() => splash.remove(), 250)
  }
  const ready = () =>
    !!app.querySelector('main, header, nav, h1, form') &&
    !app.querySelector('[role="status"][aria-label="Loading"]')
  const observer = new MutationObserver(() => {
    // A short timer, not rAF (paused in background tabs), lets the screen paint first.
    if (ready()) setTimeout(() => ready() && hide(), 60)
  })
  observer.observe(app, { childList: true, subtree: true })
  setTimeout(hide, 10_000)
}
